import {useMemo} from 'react';
import type {CalendarEvent} from './useEvents'

type Group = {
    id: string; 
    name: string; 
    events: CalendarEvent[];
    next?: CalendarEvent;
    avatar?: {name?: string; photoUrl?: string | null; color?: string | null};
  }

function counterpartyId(ev: CalendarEvent, currentUserId?: string | null) {
    if (!currentUserId) return ev.parentId || ev.tutorId;
    return ev.tutorId === currentUserId ? ev.parentId : ev.tutorId;
}

function counterpartyName(ev: CalendarEvent, currentUserId?: string | null) {
    if (!currentUserId) return ev.parentName || ev.tutorName || '';
    if (ev.tutorId === currentUserId) return ev.parentName || '';
    if (ev.parentId === currentUserId) return ev.tutorName || '';
    return ev.parentName || ev.tutorName || '';
}

export function useLessonData(
    events: CalendarEvent[],
    userId: string | undefined, 
    mode: 'upcoming' | 'completed',
) {

    const isTutorViewer = useMemo(() => {
        if (!userId || !events.length) return false;
        const tutorHits = events.filter(e => e.tutorId === userId).length;
        const parentHits = events.filter(e => e.parentId === userId).length;
        return tutorHits >= parentHits; 
    }, [events, userId]);

    const sortedList = useMemo(() => {
        const now = Date.now();
        const filtered = events.filter(e => {
            const end = new Date(e.endAt).getTime();
            return mode === 'upcoming' ? end >= now : end < now; 
        });

        return filtered.sort((a, b) => {
            const startA = new Date(a.startAt).getTime();
            const startB = new Date(b.startAt).getTime();
            return mode === 'upcoming' ? startA - startB : startB - startA;
        }); 
    }, [mode, events]);

    const groupedList = useMemo(() => {
        const map = new Map<string, Group>();
    
        sortedList.forEach(ev => {
          const cid = counterpartyId(ev, userId);
          if (!cid) return;
          const name = counterpartyName(ev, userId) || (isTutorViewer ? 'Parent' : 'Tutor');
    
          if (!map.has(cid)) {
            map.set(cid, { id: cid, name, events: [] });
          }
          map.get(cid)!.events.push(ev);
        });
    
        map.forEach(group => {
          const sorted = [...group.events].sort((a, b) =>
            mode === 'upcoming'
              ? +new Date(a.startAt) - +new Date(b.startAt)
              : +new Date(b.startAt) - +new Date(a.startAt)
          );
          group.next = sorted[0];
    
          const richName =
            sorted.map(e => counterpartyName(e, userId)).find(n => n && n.trim().length) ?? group.name;
          group.name = richName;
    
          const sample = sorted.find(Boolean);
        if (sample) {
          if (isTutorViewer) {
            // Counterparty is parent
            group.avatar = {
              name: richName,
              photoUrl: (sample as any).parentProfilePhotoUrl ?? null,
              color: (sample as any).parentProfileColor ?? null,
            };
          } else {
            // Counterparty is tutor
            group.avatar = {
              name: richName,
              photoUrl: (sample as any).tutorProfilePhotoUrl ?? sample.tutorProfilePhotoUrl ?? null,
              color: null,
            };
            }
          } 
        });
    
        return Array.from(map.values()).sort((a, b) => {
            const aT = a.next ? new Date(a.next.startAt).getTime() : 0;
            const bT = b.next ? new Date(b.next.startAt).getTime() : 0;
            return mode === 'upcoming' ? aT - bT : bT - aT;
        });
    }, [sortedList, userId, mode, isTutorViewer]);

      return {
        groupedList, 
        sortedList, 
        isTutorViewer
      }


}